import {
  Box,
  FormHelperText,
  IconButton,
  TextField,
  Typography
} from '@material-ui/core'
import ClearIcon from '@material-ui/icons/Clear'
import Autocomplete from '@material-ui/lab/Autocomplete'

import * as R from 'ramda'
import React, { FC, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useQuery } from 'react-query'
import styled from 'styled-components'

import { UserInterface } from '../../interfaces/user'
import { userApi } from '../../services'

const useDebounce = (value: string, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return debouncedValue
}

const useSearchUsers = (keyword: string) => {
  return useQuery<UserInterface[], Error>(
    ['searchCategory', keyword],
    userApi.searchUsers
  )
}

const noneSelected = 'None selected'

const UserSearchContainer = styled.div`
  margin-bottom: 40px;
`

const SelectedUserWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
`

const SelectedUserFieldWrapper = styled.div`
  display: flex;
  background-color: rgba(0, 0, 0, 0.2);
  margin-left: 20px;
  height: 35px;
  padding: 0px 15px;
  align-items: center;
  border-radius: 5px;
`

const OptionWrapper = styled.div`
  margin: 2px 0px;
`

type ModifiedUserInterface = { userId: string; name: string }

const modifiedUser = (user?: UserInterface): ModifiedUserInterface => ({
  userId: user?.userId ?? '',
  name:
    R.isEmpty(user) || R.isNil(user)
      ? noneSelected
      : `${user?.accountNo} - ${user?.email} - ${user?.firstName} ${user?.lastName}`
})

type Props = {
  fieldName: string
  fieldValue?: UserInterface
  rules?: any
  required?: boolean
}

const UserSearchAutoComplete: FC<Record<string, unknown> & Props> = ({
  fieldName,
  fieldValue,
  rules = {},
  required = false
}) => {
  const modifiedFieldValue = modifiedUser(fieldValue)
  const { control, setValue } = useFormContext()
  const [open, setOpen] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<string>(
    modifiedFieldValue.name
  )
  const [inputValue, setInputValue] = useState<string>('')
  const debouncedKeyword = useDebounce(inputValue, 600)
  const { isLoading, data = [] } = useSearchUsers(debouncedKeyword)
  const transformedData: ModifiedUserInterface[] = R.map(
    (i) => modifiedUser(i),
    data
  )

  const handleClearSelectedUser = () => {
    setValue(fieldName, null)
    setSelectedUser(noneSelected)
  }

  return (
    <UserSearchContainer>
      <SelectedUserWrapper>
        <Box fontStyle="italic" fontSize={16}>
          Selected User:
        </Box>
        <SelectedUserFieldWrapper>
          <Typography variant="subtitle2">{selectedUser}</Typography>
          {selectedUser !== noneSelected && (
            <IconButton
              onClick={handleClearSelectedUser}
              style={{ backgroundColor: 'transparent' }}
            >
              <ClearIcon />
            </IconButton>
          )}
        </SelectedUserFieldWrapper>
      </SelectedUserWrapper>
      <Controller
        control={control}
        name={fieldName}
        rules={{
          ...rules,
          required: {
            value: required,
            message: '* Your input is required'
          }
        }}
        as={
          <>
            <Autocomplete
              id={`${fieldName}-autoComplete`}
              fullWidth
              defaultValue={{
                userId: fieldValue?.userId ?? '',
                name: ''
              }}
              open={open}
              onOpen={() => {
                setOpen(true)
              }}
              onClose={() => {
                setOpen(false)
              }}
              onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input') setInputValue(newInputValue)
              }}
              getOptionLabel={(option) => option.name}
              options={transformedData}
              loading={isLoading}
              renderOption={(option) => (
                <OptionWrapper
                  onClick={() => {
                    setSelectedUser(option.name)
                    setValue(fieldName, option.userId)
                    setInputValue('')
                  }}
                >
                  {option.name}
                </OptionWrapper>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type email or name to search for user"
                  inputProps={{
                    ...params.inputProps,
                    value: inputValue
                  }}
                />
              )}
            />
            {required && <FormHelperText>Required</FormHelperText>}
          </>
        }
      />
    </UserSearchContainer>
  )
}

export default UserSearchAutoComplete
